import { Browser } from "@capacitor/browser";
import {
  WorkerRequirementDomain,
  WorkerRequirementMappingLevel,
} from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { generatePath, useHistory } from "react-router-dom";

import { ACCOUNT_ROLES_FULL_PATH } from "../../../../../Accounts/Roles/paths";
import {
  DOCUMENT_DETAILS_FULL_PATH,
  WORKPLACE_DOCUMENTS_FULL_PATH,
} from "../../../../../Documents/paths";
import { type PlacementDetail } from "../../../../api/useGetPlacement";
import { useRecruiterBookingFlowContext } from "../../../../hooks/useRecruiterBookingFlowContext";
import { type PrimaryAction } from "../../utils/getPrimaryAction";
import { type PlacementDerivedData } from "../usePlacementPrimaryAction";

interface UseNavigationOperationsParams {
  placementDetails: PlacementDetail;
  placementCandidate: PlacementCandidate;
  primaryAction: PrimaryAction;
  derivedData: PlacementDerivedData;
}

/**
 * Handles navigation operations (profile, documents, recruiter).
 */
export function useNavigationOperations(params: UseNavigationOperationsParams) {
  const { placementDetails, placementCandidate, primaryAction, derivedData } = params;

  const history = useHistory();
  const {
    requirement,
    isProfilePending,
    highestRankingSharedQualification,
    eventContext,
    screeningCalendlyLink,
    upskillingCalendlyLink,
    showCallMeNow,
    talkToRecruiterCopy,
  } = derivedData;
  const recruiterScreeningCallFlowState = useRecruiterBookingFlowContext();

  function handleUploadCredentialAction() {
    const { missingCredentialRequirementType } = derivedData;
    if (missingCredentialRequirementType === "BASE_CREDENTIAL") {
      history.push(
        generatePath(DOCUMENT_DETAILS_FULL_PATH, {
          requirementId: placementDetails.credentialRequirements?.find(
            (requirement) => requirement.attributes.level === WorkerRequirementMappingLevel.STATE
          )?.relationships.requirement.data.id,
        })
      );
    } else if (missingCredentialRequirementType === "WORKPLACE_CREDENTIAL") {
      history.push(
        generatePath(DOCUMENT_DETAILS_FULL_PATH, {
          requirementId: placementDetails.credentialRequirements?.find(
            (requirement) => requirement.attributes.level === WorkerRequirementMappingLevel.HCF
          )?.relationships.requirement.data.id,
        })
      );
    }
  }

  function handleCompleteProfileAction() {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_DETAILS_PAGE_ACTIONS, {
      action: "complete-profile-clicked",
      isProfilePending,
      requirement,
      hiringEvents: placementDetails.hiringEvents?.map((event) => {
        return {
          id: event.id,
          name: event.attributes.type,
          startDate: event.attributes.fixedSchedule?.startDate,
          endDate: event.attributes.fixedSchedule?.endDate,
        };
      }),
    });
    history.push(ACCOUNT_ROLES_FULL_PATH);
  }

  function handleUploadLicenseAction() {
    history.push(ACCOUNT_ROLES_FULL_PATH);
  }

  function handleUploadDocumentAction() {
    const requiredDocuments = placementDetails.workerRequirements?.filter(
      (requirement) => !requirement.attributes.isValid
    );
    const hasSingleRequiredDocument =
      isDefined(requiredDocuments) && requiredDocuments.length === 1;

    if (hasSingleRequiredDocument) {
      history.push(
        generatePath(DOCUMENT_DETAILS_FULL_PATH, {
          requirementId: requiredDocuments[0].relationships.requirement.data.id,
        })
      );
      return;
    }

    const workplaceDocumentsPath = generatePath(WORKPLACE_DOCUMENTS_FULL_PATH, {
      workplaceId: placementDetails.workplace.id,
    });
    const hasHighestRankingQualification = isDefined(highestRankingSharedQualification);

    const targetPath = hasHighestRankingQualification
      ? `${workplaceDocumentsPath}?qualification=${highestRankingSharedQualification}&domain=${WorkerRequirementDomain.HIRING}`
      : `${workplaceDocumentsPath}?domain=${WorkerRequirementDomain.HIRING}`;

    history.push(targetPath);
  }

  async function handleTalkToRecruiterAction() {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_TALK_TO_RECRUITER_CLICKED, {
      ...eventContext,
      placementDetails,
      placementCandidate,
      showCallMeNow,
      copyId: talkToRecruiterCopy.id,
    });

    const isUpskillingAction =
      primaryAction.label === talkToRecruiterCopy.placementAction.upskilling;

    if (isUpskillingAction) {
      logEvent(APP_V2_APP_EVENTS.PLACEMENT_COMPLETE_SKILLS_COURSE_CLICKED, {
        ...eventContext,
        placementDetails,
        placementCandidate,
        copyId: talkToRecruiterCopy.id,
      });
      await Browser.open({
        url: upskillingCalendlyLink,
        presentationStyle: "fullscreen",
      });
      return;
    }

    if (showCallMeNow) {
      recruiterScreeningCallFlowState.modalState.openModal();
      logEvent(APP_V2_APP_EVENTS.PLACEMENT_RECRUITER_SCREENING_CALL_MODAL_OPENED, {
        placementCandidateId: placementCandidate.id,
        showCallMeNow,
        copyId: talkToRecruiterCopy.id,
      });
      return;
    }

    await Browser.open({
      url: screeningCalendlyLink,
      presentationStyle: "fullscreen",
    });
  }

  return {
    handleCompleteProfileAction,
    handleUploadLicenseAction,
    handleUploadDocumentAction,
    handleTalkToRecruiterAction,
    handleUploadCredentialAction,
    recruiterScreeningCallFlowState,
  };
}

export type NavigationOperationsReturn = ReturnType<typeof useNavigationOperations>;
